import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material"
import {
    GridColDef,
    GridRenderEditCellParams,
    GridRowParams,
    useGridApiContext,
} from "@mui/x-data-grid"
import {
    ReactElement,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import { createPortal } from "react-dom"

import {
    ChangeAccordion,
    ChangeAccordionDetails,
    ChangeAccordionSummary,
} from "./changeAccordion"
import ChangeSummaryGrids from "./changeSummaryGrids"
import AdminDataGrid from "../../../components/dataDisplay/adminDataGrid/adminDataGrid"
import { useScreenSize } from "../../../hooks/useScreenSize"
import useThresholdDraft from "../../../hooks/useThresholdDraft"
import { useSubmitChangeRequest } from "../../../services/middleware/hooks/dnoThreshold/useChangeRequestActions"
import useGetCurrentConfig from "../../../services/middleware/hooks/dnoThreshold/useGetCurrentConfig"
import useGetLicenceAreaThresholds from "../../../services/middleware/hooks/dnoThreshold/useGetLicenceAreaThresholds"
import useThresholdLock from "../../../services/middleware/hooks/dnoThreshold/useThresholdLock"
import { IChangeDetail } from "../../../services/middleware/types/dnoThreshold/dnoThresholdTypes"
import {
    getDisplayName,
    getFieldDescription,
} from "../../../utils/thresholdDictionary/thresholdDictionary"

// --- Value type helpers ---

type ValueType = "boolean" | "number" | "string"

const detectValueType = (value: unknown): ValueType => {
    if (typeof value === "boolean") return "boolean"
    if (typeof value === "number") return "number"
    return "string"
}

const parseToType = (
    raw: string,
    valueType: ValueType
): string | number | boolean => {
    if (valueType === "boolean") return raw === "true"
    if (valueType === "number") return Number(raw)
    return raw
}

const SKIP_CHECK_VALUE = -1

// Business-rule upper bound — values at or above this are rejected
const MAX_THRESHOLD_VALUE = 9999

const COMMENT_MAX_LENGTH = 500

/** Returns an error message if `raw` is not a valid number, or "" if valid. */
const getNumberError = (raw: string): string => {
    if (raw.trim() === "") return "Value is required"
    const n = Number(raw)
    if (Number.isNaN(n) || !Number.isFinite(n)) return "Must be a valid number"
    if (n < 0 && n !== SKIP_CHECK_VALUE)
        return `Negative values are not allowed (except ${SKIP_CHECK_VALUE})`
    if (n >= MAX_THRESHOLD_VALUE)
        return `Value must be less than ${MAX_THRESHOLD_VALUE.toLocaleString()}`
    return ""
}

const isValidNumber = (raw: string): boolean => getNumberError(raw) === ""

/** Round a number to at most 4 decimal places */
const roundTo4dp = (n: number): number => Number(n.toFixed(4))

const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
}

// --- Editable row types ---

interface EditableConfigRow {
    name: string
    currentValue: string | number | boolean | null
    newValue: string | number | boolean | null
}

interface EditableThresholdRow {
    id: string
    thresholdId: string
    name: string
    currentValue: string | number | boolean | null
    newValue: string | number | boolean | null
}

/**
 * Custom edit cell that renders the appropriate control
 * based on the original value type (boolean → Select,
 * number/string → TextField).
 */
const ValueEditCell = ({
    row,
    id,
    field,
    value,
}: GridRenderEditCellParams): ReactElement => {
    const apiRef = useGridApiContext()
    const origType = detectValueType(row.currentValue)

    const handleChange = (newValue: unknown): void => {
        apiRef.current.setEditCellValue({
            id,
            field,
            value: newValue,
        })
    }

    if (origType === "boolean") {
        return (
            <Select<string>
                value={String(value ?? "")}
                onChange={(e): void => {
                    handleChange(e.target.value === "true")
                }}
                size="small"
                fullWidth
                autoFocus
            >
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
            </Select>
        )
    }

    return (
        <TextField
            value={value ?? ""}
            onChange={(e): void => handleChange(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            margin="none"
            inputProps={{
                inputMode: origType === "number" ? "decimal" : "text",
            }}
            sx={{ "& .MuiInputBase-root": { height: "100%" } }}
        />
    )
}

// --- Mobile value editor (tap-friendly, no double-click) ---

type MobileValueEditorProps = {
    currentValue: unknown
    newValue: unknown
    onChange: (val: string | number | boolean) => void
}

/**
 * Touch-friendly inline editor for mobile/tablet.
 * Booleans render a Select; numbers/strings render a TextField.
 * Numbers are validated on blur and committed to parent state.
 */
const MobileValueEditor = memo(
    ({
        currentValue,
        newValue,
        onChange,
    }: MobileValueEditorProps): ReactElement => {
        const origType = detectValueType(currentValue)
        const [local, setLocal] = useState(String(newValue ?? ""))
        const [error, setError] = useState("")

        useEffect(() => {
            setLocal(String(newValue ?? ""))
        }, [newValue])

        const commitValue = (): void => {
            if (origType === "number") {
                const err = getNumberError(local)
                if (err) {
                    setError(err)
                    return
                }
                setError("")
                onChange(roundTo4dp(Number(local)))
            } else {
                onChange(local)
            }
        }

        if (origType === "boolean") {
            return (
                <Select<string>
                    value={String(newValue ?? "")}
                    onChange={(e): void => {
                        onChange(e.target.value === "true")
                    }}
                    size="small"
                    fullWidth
                >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                </Select>
            )
        }

        return (
            <TextField
                value={local}
                onChange={(e): void => setLocal(e.target.value)}
                onBlur={commitValue}
                size="small"
                fullWidth
                error={!!error}
                helperText={error || undefined}
                inputProps={{
                    inputMode: origType === "number" ? "decimal" : "text",
                }}
            />
        )
    }
)
MobileValueEditor.displayName = "MobileValueEditor"

// --- Props ---

type InitiateChangeAccordionProps = {
    isThresholdManager: boolean
    username: string
    onSubmitSuccess: () => void
    buttonSlotEl: HTMLDivElement | null
    hasPendingChange: boolean
}

/**
 * Inline accordion for initiating a threshold / config change.
 * Replaces the old popup dialog — renders editable DataGrids
 * for DNO Configuration and Licence Area Thresholds.
 */
const InitiateChangeAccordion = ({
    isThresholdManager,
    username,
    onSubmitSuccess,
    buttonSlotEl,
    hasPendingChange,
}: InitiateChangeAccordionProps): ReactElement | null => {
    const { isTabletOrMob } = useScreenSize()

    // Data hooks
    const { data: configData, isLoading: configLoading } = useGetCurrentConfig()
    const { data: thresholdData, isLoading: thresholdLoading } =
        useGetLicenceAreaThresholds()

    // Lock hook
    const {
        remainingSeconds,
        acquireLock,
        releaseLock,
        lockStatus,
        isLockOwner,
        error: lockError,
    } = useThresholdLock(username)
    const lockStatusRef = useRef(lockStatus)
    lockStatusRef.current = lockStatus

    // Submit hook
    const {
        submitChange,
        isSubmitting,
        error: submitError,
    } = useSubmitChangeRequest()

    // Local state
    const [isEditing, setIsEditing] = useState(false)
    const [isAcquiring, setIsAcquiring] = useState(false)
    const [lockAcquired, setLockAcquired] = useState(false)
    const [lockFailMessage, setLockFailMessage] = useState<string | null>(null)
    const [title, setTitle] = useState("")
    const [comment, setComment] = useState("")
    const [editableConfig, setEditableConfig] = useState<EditableConfigRow[]>(
        []
    )
    const [editableThresholds, setEditableThresholds] = useState<
        EditableThresholdRow[]
    >([])
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [validationFlash, setValidationFlash] = useState<string | null>(null)
    const editInitialisedRef = useRef(false)

    // Draft persistence
    const { saveDraft, loadDraft, clearDraft } = useThresholdDraft<
        EditableConfigRow,
        EditableThresholdRow
    >(username)
    const draftRestoredRef = useRef(false)

    // Restore draft when the user returns and still owns the lock
    useEffect(() => {
        if (isLockOwner && !isEditing && !draftRestoredRef.current) {
            const draft = loadDraft()
            if (draft) {
                setTitle(draft.title)
                setComment(draft.comment)
                setEditableConfig(draft.editableConfig)
                setEditableThresholds(draft.editableThresholds)
                setIsEditing(true)
                setLockAcquired(true)
                editInitialisedRef.current = true
                draftRestoredRef.current = true
            }
        }
    }, [isLockOwner, isEditing, loadDraft])

    // Auto-save draft on changes (debounced 500ms)
    useEffect(() => {
        if (!isEditing) return undefined
        const timer = setTimeout(() => {
            saveDraft({
                title,
                comment,
                editableConfig,
                editableThresholds,
            })
        }, 500)
        return (): void => clearTimeout(timer)
    }, [
        isEditing,
        title,
        comment,
        editableConfig,
        editableThresholds,
        saveDraft,
    ])

    // Save draft immediately on page unload
    useEffect(() => {
        if (!isEditing) return undefined
        const handleBeforeUnload = (): void => {
            saveDraft({
                title,
                comment,
                editableConfig,
                editableThresholds,
            })
        }
        window.addEventListener("beforeunload", handleBeforeUnload)
        return (): void => {
            window.removeEventListener("beforeunload", handleBeforeUnload)
        }
    }, [
        isEditing,
        title,
        comment,
        editableConfig,
        editableThresholds,
        saveDraft,
    ])

    // Populate editable rows only once when editing starts
    useEffect(() => {
        if (isEditing && configData && !editInitialisedRef.current) {
            setEditableConfig(
                configData.config.map((item) => ({
                    name: item.name,
                    currentValue: item.value,
                    newValue: item.value,
                }))
            )
        }
    }, [isEditing, configData])

    useEffect(() => {
        if (isEditing && thresholdData && !editInitialisedRef.current) {
            setEditableThresholds(
                Object.entries(thresholdData.thresholds).flatMap(
                    ([thresholdId, items]) =>
                        items.map((t) => ({
                            id: `${thresholdId}-${t.name}`,
                            thresholdId,
                            name: t.name,
                            currentValue: t.value,
                            newValue: t.value,
                        }))
                )
            )
        }
    }, [isEditing, thresholdData])

    // Mark as initialised once both are populated
    useEffect(() => {
        if (isEditing && configData && thresholdData) {
            editInitialisedRef.current = true
        }
        if (!isEditing) {
            editInitialisedRef.current = false
        }
    }, [isEditing, configData, thresholdData])

    // --- Lock management ---

    const handleStartEdit = useCallback(async (): Promise<void> => {
        setIsAcquiring(true)
        setLockFailMessage(null)
        const success = await acquireLock()
        setIsAcquiring(false)
        if (success) {
            setLockAcquired(true)
            setIsEditing(true)
        } else {
            const currentLock = lockStatusRef.current
            setLockFailMessage(
                currentLock?.isLocked
                    ? `Configuration is being edited by ${currentLock.lockedByName} for another
                     ${formatCountdown(currentLock.remainingSeconds)} minutes. Please try again later.`
                    : "Failed to acquire editing lock. Please try again."
            )
        }
    }, [acquireLock])

    const handleCancel = useCallback((): void => {
        if (lockAcquired) releaseLock()
        clearDraft()
        draftRestoredRef.current = false
        setIsEditing(false)
        setLockAcquired(false)
        setTitle("")
        setComment("")
        setEditableConfig([])
        setEditableThresholds([])
        setShowConfirmation(false)
        setLockFailMessage(null)
    }, [lockAcquired, releaseLock, clearDraft])

    // Auto-close on lock expiry
    useEffect(() => {
        if (lockAcquired && remainingSeconds <= 0) {
            handleCancel()
        }
    }, [remainingSeconds, lockAcquired, handleCancel])

    // --- Change computation ---

    const pendingChanges: IChangeDetail[] = useMemo(() => {
        const changes: IChangeDetail[] = []

        const coerce = (
            raw: unknown,
            current: unknown
        ): string | number | boolean | null => {
            const t = detectValueType(current)
            return parseToType(String(raw), t)
        }

        editableConfig.forEach((row) => {
            const typed = coerce(row.newValue, row.currentValue)
            if (typed === row.currentValue) return
            changes.push({
                field: row.name,
                oldValue: row.currentValue,
                newValue: typed,
            })
        })

        editableThresholds.forEach((row) => {
            const typed = coerce(row.newValue, row.currentValue)
            if (typed === row.currentValue) return
            changes.push({
                key: row.thresholdId,
                field: row.name,
                oldValue: row.currentValue,
                newValue: typed,
            })
        })

        return changes
    }, [editableConfig, editableThresholds])

    const isTitleValid = title.length > 0 && title.length <= 50
    const isCommentValid =
        comment.trim().length > 0 && comment.length <= COMMENT_MAX_LENGTH

    const hasValidationErrors = useMemo((): boolean => {
        const hasError = (current: unknown, next: unknown): boolean => {
            if (current === next) return false
            const origType = detectValueType(current)
            return origType === "number" && !isValidNumber(String(next))
        }
        return (
            editableConfig.some((r) => hasError(r.currentValue, r.newValue)) ||
            editableThresholds.some((r) => hasError(r.currentValue, r.newValue))
        )
    }, [editableConfig, editableThresholds])

    const isSubmitDisabled =
        !isTitleValid ||
        !isCommentValid ||
        pendingChanges.length === 0 ||
        hasValidationErrors

    const submitDisabledReason = useMemo((): string => {
        const reasons: string[] = []
        if (!isTitleValid) reasons.push("Title is required")
        if (!isCommentValid) reasons.push("Comment is required")
        if (pendingChanges.length === 0)
            reasons.push("No changes have been made")
        if (hasValidationErrors) reasons.push("Fix validation errors first")
        return reasons.join(". ")
    }, [
        isTitleValid,
        isCommentValid,
        pendingChanges.length,
        hasValidationErrors,
    ])

    // --- Submit ---

    const handleSubmit = useCallback(async (): Promise<void> => {
        const success = await submitChange(title, comment, pendingChanges)
        if (success) {
            await releaseLock()
            handleCancel()
            onSubmitSuccess()
        }
    }, [
        submitChange,
        title,
        comment,
        pendingChanges,
        releaseLock,
        handleCancel,
        onSubmitSuccess,
    ])

    // --- Row update handlers (cell editing) ---

    const validateAndCoerce = <
        T extends { currentValue: unknown; newValue: unknown },
    >(
        newRow: T,
        oldRow: T
    ): T | null => {
        const origType = detectValueType(newRow.currentValue)
        let { newValue } = newRow

        if (origType === "number") {
            const raw = String(newValue)
            const error = getNumberError(raw)
            if (error) {
                setValidationFlash(error)
                return null
            }
            newValue = roundTo4dp(Number(raw))
        }
        if (origType === "boolean") {
            newValue = parseToType(String(newValue), "boolean")
        }

        return { ...newRow, newValue }
    }

    const handleConfigRowUpdate = useCallback(
        (
            newRow: EditableConfigRow,
            oldRow: EditableConfigRow
        ): EditableConfigRow => {
            const updated = validateAndCoerce(newRow, oldRow)
            if (!updated) return oldRow

            setEditableConfig((prev) =>
                prev.map((r) => (r.name === updated.name ? updated : r))
            )
            return updated
        },
        []
    )

    const handleThresholdRowUpdate = useCallback(
        (
            newRow: EditableThresholdRow,
            oldRow: EditableThresholdRow
        ): EditableThresholdRow => {
            const updated = validateAndCoerce(newRow, oldRow)
            if (!updated) return oldRow

            setEditableThresholds((prev) =>
                prev.map((r) => (r.id === updated.id ? updated : r))
            )
            return updated
        },
        []
    )

    // --- Mobile change handlers ---

    const handleMobileConfigChange = useCallback(
        (name: string, val: string | number | boolean): void => {
            setEditableConfig((prev) =>
                prev.map((r) => (r.name === name ? { ...r, newValue: val } : r))
            )
        },
        []
    )

    const handleMobileThresholdChange = useCallback(
        (id: string, val: string | number | boolean): void => {
            setEditableThresholds((prev) =>
                prev.map((r) => (r.id === id ? { ...r, newValue: val } : r))
            )
        },
        []
    )

    /** Thresholds grouped by thresholdId (licence area) for mobile card layout */
    const groupedThresholds = useMemo(() => {
        const groups: Record<string, EditableThresholdRow[]> = {}
        editableThresholds.forEach((t) => {
            if (!groups[t.thresholdId]) groups[t.thresholdId] = []
            groups[t.thresholdId].push(t)
        })
        return groups
    }, [editableThresholds])

    /** Highlight rows where the new value differs from the current value */
    const getChangedRowClassName = useCallback(
        (params: GridRowParams): string => {
            const { currentValue, newValue } = params.row
            return String(currentValue) !== String(newValue)
                ? "row-changed"
                : ""
        },
        []
    )

    // --- Column definitions ---

    const theme = useTheme()

    const renderNewValueCell = useCallback(
        (
            { row, value }: GridRenderEditCellParams,
            unchangedWeight: number
        ): ReactElement => {
            const changed = String(row.currentValue) !== String(value)
            const origType = detectValueType(row.currentValue)
            const error =
                changed && origType === "number"
                    ? getNumberError(String(value))
                    : ""
            return (
                <Tooltip title={error} arrow>
                    <span
                        style={{
                            fontWeight: changed ? 700 : unchangedWeight,
                            color: error ? theme.palette.error.main : undefined,
                        }}
                    >
                        {String(value ?? "—")}
                    </span>
                </Tooltip>
            )
        },
        [theme.palette.error.main]
    )

    const configColumns: GridColDef[] = useMemo(
        () => [
            {
                field: "name",
                headerName: "Setting",
                flex: 1,
                valueFormatter: (value: unknown): string =>
                    getDisplayName(String(value)),
                renderCell: ({
                    value,
                }: GridRenderEditCellParams): ReactElement => {
                    const display = getDisplayName(String(value))
                    const desc = getFieldDescription(String(value))
                    const span = (
                        <span
                            style={{
                                whiteSpace: "normal",
                                lineHeight: 1.4,
                            }}
                        >
                            {display}
                        </span>
                    )
                    return desc ? (
                        <Tooltip title={desc} arrow>
                            {span}
                        </Tooltip>
                    ) : (
                        span
                    )
                },
            },
            {
                field: "currentValue",
                headerName: "Current Value",
                flex: 1,
                valueFormatter: (value: unknown): string =>
                    String(value ?? "—"),
            },
            {
                field: "newValue",
                headerName: "New Value",
                flex: 1,
                editable: true,
                renderEditCell: (
                    params: GridRenderEditCellParams
                ): ReactElement => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <ValueEditCell {...params} />
                ),
                renderCell: (params: GridRenderEditCellParams): ReactElement =>
                    renderNewValueCell(params, 400),
                valueFormatter: (value: unknown): string =>
                    String(value ?? "—"),
            },
        ],
        [renderNewValueCell]
    )

    const thresholdColumns: GridColDef[] = useMemo(
        () => [
            {
                field: "thresholdId",
                headerName: "Licence Area",
                flex: 0.5,
            },
            {
                field: "name",
                headerName: "Threshold",
                flex: 2,
                valueFormatter: (value: unknown): string =>
                    getDisplayName(String(value)),
                renderCell: ({
                    value,
                }: GridRenderEditCellParams): ReactElement => {
                    const display = getDisplayName(String(value))
                    const desc = getFieldDescription(String(value))
                    const span = (
                        <span
                            style={{
                                whiteSpace: "normal",
                                lineHeight: 1.4,
                            }}
                        >
                            {display}
                        </span>
                    )
                    return desc ? (
                        <Tooltip title={desc} arrow>
                            {span}
                        </Tooltip>
                    ) : (
                        span
                    )
                },
            },
            {
                field: "currentValue",
                headerName: "Current Value",
                flex: 1,
                valueFormatter: (value: unknown): string =>
                    String(value ?? "—"),
            },
            {
                field: "newValue",
                headerName: "New Value",
                flex: 1,
                editable: true,
                renderEditCell: (
                    params: GridRenderEditCellParams
                ): ReactElement => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <ValueEditCell {...params} />
                ),
                renderCell: (params: GridRenderEditCellParams): ReactElement =>
                    renderNewValueCell(params, 400),
                valueFormatter: (value: unknown): string =>
                    String(value ?? "—"),
            },
        ],
        [renderNewValueCell]
    )

    const initiateDisabledReason = useMemo((): string => {
        if (lockStatus?.isLocked)
            return "Configuration is currently locked for editing"
        if (hasPendingChange)
            return "A change request is already pending approval"
        return ""
    }, [lockStatus?.isLocked, hasPendingChange])

    if (!isThresholdManager) return null

    return (
        <>
            {/* Initiate button — portaled into tab bar slot */}
            {!isEditing &&
                buttonSlotEl &&
                createPortal(
                    <Tooltip title={initiateDisabledReason}>
                        <span>
                            <Button
                                variant="contained"
                                onClick={handleStartEdit}
                                disabled={
                                    isAcquiring ||
                                    hasPendingChange ||
                                    !!lockStatus?.isLocked
                                }
                                sx={{
                                    borderRadius: 6,
                                    textTransform: "none",
                                    px: 3,
                                    py: 1,
                                }}
                                startIcon={
                                    isAcquiring ? (
                                        <CircularProgress size={16} />
                                    ) : undefined
                                }
                            >
                                {isAcquiring
                                    ? "Acquiring Lock..."
                                    : "Initiate Change"}
                            </Button>
                        </span>
                    </Tooltip>,
                    buttonSlotEl
                )}

            {/* Lock alerts — inline below tabs */}
            {!isEditing && lockFailMessage && (
                <Alert
                    severity="warning"
                    sx={{ mb: 1 }}
                    onClose={(): void => setLockFailMessage(null)}
                >
                    {lockFailMessage}
                </Alert>
            )}
            {!isEditing && lockError && !lockFailMessage && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {lockError}
                </Alert>
            )}

            {/* Editing accordion */}
            {isEditing && (
                <ChangeAccordion
                    defaultExpanded
                    sx={{ mb: 2, "&.Mui-expanded": { mb: 2 } }}
                >
                    <ChangeAccordionSummary>
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{ width: "100%" }}
                        >
                            <Typography fontWeight="500">
                                Editing Configuration
                            </Typography>
                            <Tooltip
                                title="Your editing lock expires when
                                    this timer reaches zero. Unsaved
                                    changes will be lost."
                                arrow
                                placement="left"
                            >
                                <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                    sx={() => ({
                                        ml: "auto !important",
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 2,
                                        backgroundColor:
                                            remainingSeconds < 60
                                                ? `${theme.palette.error.main}14`
                                                : `${theme.palette.primary.main}14`,
                                    })}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color={
                                            remainingSeconds < 60
                                                ? "error"
                                                : "primary"
                                        }
                                        sx={{
                                            fontVariantNumeric: "tabular-nums",
                                            fontSize: "1.5rem",
                                        }}
                                    >
                                        {formatCountdown(remainingSeconds)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Change request times out after 10
                                        minutes
                                    </Typography>
                                </Stack>
                            </Tooltip>
                        </Stack>
                    </ChangeAccordionSummary>
                    <ChangeAccordionDetails>
                        <TextField
                            label="Change Title"
                            value={title}
                            onChange={(e): void => setTitle(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            inputProps={{ maxLength: 50 }}
                            helperText={`${title.length}/50 characters`}
                        />

                        {/* --- Desktop: DataGrid editing --- */}
                        {!isTabletOrMob && (
                            <>
                                <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                                    Double-click any <strong>New Value</strong>{" "}
                                    cell to edit it. Changed rows are
                                    highlighted in orange.
                                </Alert>
                                <AdminDataGrid<EditableConfigRow>
                                    rows={editableConfig}
                                    columns={configColumns}
                                    getRowId={(row): string => row.name}
                                    itemTypeText="DNO Configurations"
                                    isLoading={configLoading}
                                    showActions={false}
                                    toolbarButtons={[
                                        "columns",
                                        "reorder",
                                        "filter",
                                        "export",
                                    ]}
                                    processRowUpdate={handleConfigRowUpdate}
                                    initialPageSize={50}
                                    initialSortField="name"
                                    getRowClassName={getChangedRowClassName}
                                />
                                <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                                    Double-click any <strong>New Value</strong>{" "}
                                    cell to edit it. Changed rows are
                                    highlighted in orange.
                                </Alert>
                                <AdminDataGrid<EditableThresholdRow>
                                    rows={editableThresholds}
                                    columns={thresholdColumns}
                                    getRowId={(row): string => row.id}
                                    itemTypeText="Thresholds by Licence Area"
                                    isLoading={thresholdLoading}
                                    showActions={false}
                                    toolbarButtons={[
                                        "columns",
                                        "reorder",
                                        "filter",
                                        "export",
                                    ]}
                                    processRowUpdate={handleThresholdRowUpdate}
                                    initialPageSize={50}
                                    initialSortField="thresholdId"
                                    getRowClassName={getChangedRowClassName}
                                />
                            </>
                        )}

                        {/* --- Mobile/Tablet: card-based editing --- */}
                        {isTabletOrMob && (
                            <>
                                {configLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={120}
                                    />
                                ) : (
                                    editableConfig.map((item) => (
                                        <Paper
                                            key={item.name}
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                mb: 1,
                                                ...(String(
                                                    item.currentValue
                                                ) !== String(item.newValue)
                                                    ? {
                                                          backgroundColor:
                                                              "rgba(255, 152, 0, 0.08)",
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            <Tooltip
                                                title={getFieldDescription(
                                                    item.name
                                                )}
                                                arrow
                                                disableHoverListener={
                                                    !getFieldDescription(
                                                        item.name
                                                    )
                                                }
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                >
                                                    {getDisplayName(item.name)}
                                                </Typography>
                                            </Tooltip>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Current:{" "}
                                                {String(
                                                    item.currentValue ?? "—"
                                                )}
                                            </Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                <MobileValueEditor
                                                    currentValue={
                                                        item.currentValue
                                                    }
                                                    newValue={item.newValue}
                                                    onChange={(val): void =>
                                                        handleMobileConfigChange(
                                                            item.name,
                                                            val
                                                        )
                                                    }
                                                />
                                            </Box>
                                        </Paper>
                                    ))
                                )}
                                {thresholdLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={200}
                                    />
                                ) : (
                                    Object.entries(groupedThresholds).map(
                                        ([thresholdId, items]) => (
                                            <Box
                                                key={thresholdId}
                                                sx={{ mb: 2 }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    color="text.secondary"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {thresholdId}
                                                </Typography>
                                                {items.map((item) => (
                                                    <Paper
                                                        key={item.id}
                                                        variant="outlined"
                                                        sx={{
                                                            p: 1.5,
                                                            mb: 1,
                                                            ...(String(
                                                                item.currentValue
                                                            ) !==
                                                            String(
                                                                item.newValue
                                                            )
                                                                ? {
                                                                      backgroundColor:
                                                                          "rgba(255, 152, 0, 0.08)",
                                                                  }
                                                                : {}),
                                                        }}
                                                    >
                                                        <Tooltip
                                                            title={getFieldDescription(
                                                                item.name
                                                            )}
                                                            arrow
                                                            disableHoverListener={
                                                                !getFieldDescription(
                                                                    item.name
                                                                )
                                                            }
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                sx={{
                                                                    wordBreak:
                                                                        "break-word",
                                                                }}
                                                            >
                                                                {getDisplayName(
                                                                    item.name
                                                                )}
                                                            </Typography>
                                                        </Tooltip>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Current:{" "}
                                                            {String(
                                                                item.currentValue ??
                                                                    "—"
                                                            )}
                                                        </Typography>
                                                        <Box sx={{ mt: 0.5 }}>
                                                            <MobileValueEditor
                                                                currentValue={
                                                                    item.currentValue
                                                                }
                                                                newValue={
                                                                    item.newValue
                                                                }
                                                                onChange={(
                                                                    val
                                                                ): void =>
                                                                    handleMobileThresholdChange(
                                                                        item.id,
                                                                        val
                                                                    )
                                                                }
                                                            />
                                                        </Box>
                                                    </Paper>
                                                ))}
                                            </Box>
                                        )
                                    )
                                )}
                            </>
                        )}

                        <TextField
                            label="Comment"
                            value={comment}
                            onChange={(e): void => setComment(e.target.value)}
                            fullWidth
                            required
                            multiline
                            rows={3}
                            size="small"
                            inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
                            helperText={`${comment.length}/${COMMENT_MAX_LENGTH} characters`}
                            error={comment.length > COMMENT_MAX_LENGTH}
                        />

                        {pendingChanges.length > 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                {pendingChanges.length} change
                                {pendingChanges.length !== 1 ? "s" : ""} pending
                            </Alert>
                        )}

                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={2}
                            justifyContent="flex-end"
                            sx={{ mt: 2 }}
                        >
                            <Button
                                onClick={handleCancel}
                                fullWidth={isTabletOrMob}
                            >
                                Cancel
                            </Button>
                            <Tooltip
                                title={
                                    isSubmitDisabled ? submitDisabledReason : ""
                                }
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        disabled={isSubmitDisabled}
                                        onClick={(): void =>
                                            setShowConfirmation(true)
                                        }
                                        fullWidth={isTabletOrMob}
                                    >
                                        Submit for Approval
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </ChangeAccordionDetails>
                </ChangeAccordion>
            )}

            {/* Confirmation dialog */}
            {showConfirmation && (
                <Dialog
                    open={showConfirmation}
                    onClose={(): void => setShowConfirmation(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Confirm Change Request</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={1} sx={{ mb: 2 }}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Title
                            </Typography>
                            <Typography variant="body1">{title}</Typography>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Comment
                            </Typography>
                            <Typography variant="body1">
                                {comment || "—"}
                            </Typography>
                        </Stack>

                        <ChangeSummaryGrids changes={pendingChanges} />

                        {submitError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {submitError}
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={(): void => setShowConfirmation(false)}
                            disabled={isSubmitting}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? "Submitting..."
                                : "Confirm & Submit"}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <Snackbar
                open={!!validationFlash}
                autoHideDuration={5000}
                onClose={(): void => setValidationFlash(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    onClose={(): void => setValidationFlash(null)}
                >
                    {validationFlash}
                </Alert>
            </Snackbar>
        </>
    )
}

export default InitiateChangeAccordion
